<template>
  <b-card>
    <b-row>
      <b-col v-if="hasPermission('risks', 'create')" sm="8" class="pb-1">
        <b-button
          variant="outline-primary"
          :to="{ name: 'organization-process-add' }"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          {{ $t('message.Add_Process') }}
        </b-button>
      </b-col>

      <b-col sm="4" :offset-sm="hasPermission('risks', 'create') ? 0 : 8">
        <b-form-group>
          <b-form-input
            :placeholder="$t('message.search')"
            type="search"
            class="d-inline-block"
            v-model="searchQuery"
          />
        </b-form-group>
      </b-col>

      <b-col cols="12">
        <filters
          :companyFilterShow="true"
          :locationFilterShow="true"
          :activityFilterShow="true"
          :processResponsibleFilterShow="true"
          :processFilterShow="true"
          :crimeFilterShow="true"
          :controlResponsibleFilterShow="true"
          :controlFilterShow="true"
          :processTypeFilterShow="true"
          :inherentRiskFilterShow="true"
          :residualRiskFilterShow="true"
          @changeFilter="updateFilter"
        />
      </b-col>

      <b-col cols="12">
        <b-table
          ref="refProcessListTable"
          class="process-list-table"
          :items="processes"
          hover
          responsive
          :fields="tableColumns"
          primary-key="_id"
          show-empty=""
          :empty-text="isLoading ? $t('message.loading') : $t('message.no_records')"
          :sort-by.sync="sortBy"
          :sort-desc.sync="isSortDirDesc"
        >
          <template #cell(name)="data">
            <span
              v-if="data.item.name && data.item.name.length > 50"
              v-b-popover.focus.top="textPopover(data.item.name)"
              class="text-popover-trigger"
              tabindex="0"
            >
              {{ data.item.name.slice(0, 50) }}...
              <feather-icon
                icon="EyeIcon"
                size="14"
                class="ml-25"
              />
            </span>
            <span v-else>{{ data.item.name || '-' }}</span>
          </template>

          <template #cell(inherentRisk)="data">
            <span class="font-weight-bold" :style="{ color: residualRiskColors[data.item.inherentRisk || 'medium'] }">
              {{ data.item.inherentRiskComputed.value || '-' }}
            </span>
          </template>

          <template #cell(residualRisk)="data">
            <span class="font-weight-bold" :style="{ color: residualRiskColors[data.item.residualRisk || 'medium'] }">
              {{ data.item.residualRiskComputed.value || '-' }}
            </span>
          </template>

          <template #cell(controlsData)="data">
            <span
              v-if="data.item.controlsData && data.item.controlsData.length"
              v-b-popover.focus.top="controlsAndResponsiblesPopover(data.item.controlsData)"
              class="controls-count-trigger"
              tabindex="0"
            >
              {{ $tc('message.controlsCount', data.item.controlsData.length, { count: data.item.controlsData.length }) }}
              <feather-icon icon="EyeIcon" size="14" class="ml-25" />
            </span>
            <span v-else>-</span>
          </template>

          <template #cell(crimes)="data">
            <span
              v-if="data.item.crimes && data.item.crimes.length > 50"
              v-b-popover.focus.top="textPopover(data.item.crimes)"
              class="text-popover-trigger"
              tabindex="0"
            >
              {{ data.item.crimes.slice(0, 50) }}...
              <feather-icon icon="EyeIcon" size="14" class="ml-25" />
            </span>
            <span v-else>{{ data.item.crimes || '-' }}</span>
          </template>

          <template #cell(actions)="data">
            <div class="actions-grid">
              <NavigationButton
                v-if="hasPermission('risks', 'view')"
                @clicked="router.push({ name:'habit-confirmation-new', params: { id: data.item._id } })"
                i18nHoverVariable="Evaluate"
              />
              <RecordButton
                v-if="hasPermission('risks', 'view')"
                v-b-modal="`rr-history-modal-${data.item._id}`"
              />
              <ViewButton v-if="role === 'consultant' || data.item.viewButtonEnabled" variant="info" @clicked="router.push({name:'organization-process-view', params: { id: data.item._id } })" />
              <EditButton v-if="hasPermission('risks', 'edit')" @clicked="router.push({name:'organization-process-edit', params: { id: data.item._id } })" />
              <template v-if="hasPermission('risks', 'delete')">
                <DeleteButton v-b-modal="`modal ${data.item._id}`"/>
                <b-modal
                  :id="`modal ${data.item._id}`"
                  centered
                  no-close-on-backdrop
                  modal-class="modal-danger"
                  ok-variant="danger"
                  cancel-variant="outline-secondary"
                  :title="$t('message.confirm_action')"
                  :ok-title="$t('message.delete')"
                  :cancel-title="$t('message.cancel')"
                  @ok="deleteProcess(data.item._id)"
                >
                  {{ $t('message.confirm_delete_process') }}
                </b-modal>
              </template>

              <!-- Modal de historial de RR -->
              <r-r-history-modal
                :process-id="data.item._id"
                :modal-id="`rr-history-modal-${data.item._id}`"
              />
            </div>
          </template>
        </b-table>
      </b-col>

      <b-col cols="12" class="d-flex justify-content-between flex-wrap">
        <div class="mb-1">
          <b-form-group class="mb-0" >
            <label class="d-inline-block text-sm-left mr-50">{{ $t('message.per_page') }}</label>
            <b-form-select
              id="perPageSelect"
              v-model="perPage"
              size="sm"
              :options="perPageOptions"
              class="w-50"
            />
          </b-form-group>
        </div>

        <div class="mb-1">
          <span class="text-muted">{{ showingMessage }}</span>
        </div>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalProcesses"
          :per-page="perPage"
          first-number=""
          last-number=""
          prev-class="prev-item"
          next-class="next-item"
        />
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import { BPagination, BTable, BModal, BButton, VBModal, VBTooltip } from 'bootstrap-vue'
import { ref, onMounted, onUnmounted, watch, computed } from '@vue/composition-api'
import { useRouter } from '@core/utils/utils'
import EditButton from '@/views/components/Shared/Buttons/EditButton.vue'
import ViewButton from '@/views/components/Shared/Buttons/ViewButton.vue'
import DeleteButton from '@/views/components/Shared/Buttons/DeleteButton.vue'
import NavigationButton from '@/views/components/Shared/Buttons/NavigationButton.vue'
import RecordButton from '@/views/components/Shared/Buttons/RecordButton.vue'
import RRHistoryModal from './RRHistoryModal.vue'
import useProcessList from './useProcessList'
import { residualRiskColors } from '@/constants'
import Filters from "@/views/organization/Filters.vue";
import i18n from '@/libs/i18n'
import { hasPermission } from '@/composables/usePermission'
import store from '@/store'

export default {
  components: {
    BTable,
    BPagination,
    BButton,
    EditButton,
    ViewButton,
    DeleteButton,
    NavigationButton,
    RecordButton,
    RRHistoryModal,
    BModal,
    Filters,
  },
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
  },
  setup() {
    const { router } = useRouter()
    const { role } = store.state?.userStore?.userData

    const {
      fetchProcesses,
      tableColumns,
      perPage,
      currentPage,
      perPageOptions,
      totalProcesses,
      showingMessage,
      searchQuery,
      sortBy,
      isSortDirDesc,
      processes,
      deleteProcess,
      isLoading,
      updateFilter,
    } = useProcessList()

    onMounted(() => {
      fetchProcesses()
    })

    const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, char => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[char]))

    const controlsAndResponsiblesPopoverHtml = controls => {
      const items = controls.map(control => {
        const title = escapeHtml(control?.name || '-')
        const responsibleName = control?.responsible

        const responsibleHtml = responsibleName
          ? `<strong class="text-primary">${escapeHtml(responsibleName)}</strong>`
          : `<span class="text-danger">${escapeHtml(i18n.t('message.noResponsible'))}</span>`

        return `<li>${title} - ${responsibleHtml}</li>`
      }).join('')

      return `<ol class="controls-popover-list">${items}</ol>`
    }

    const controlsAndResponsiblesPopover = controls => ({
      html: true,
      content: controlsAndResponsiblesPopoverHtml(controls),
      customClass: 'controls-responsibles-popover',
    })

    const textPopover = text => ({
      content: text,
      customClass: 'controls-responsibles-popover',
    })

    return {
      hasPermission,
      role,
      controlsAndResponsiblesPopover,
      textPopover,
      fetchProcesses,
      processes,
      showingMessage,
      router,
      tableColumns,
      searchQuery,
      sortBy,
      isSortDirDesc,
      deleteProcess,
      totalProcesses,
      currentPage,
      perPageOptions,
      perPage,
      isLoading,
      residualRiskColors,
      updateFilter,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.process-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}

.form-group {
  label {
    font-size: 0.85rem;
    font-weight: 400;
  }
}

.controls-count-trigger {
  cursor: pointer;
  white-space: nowrap;
}

.text-popover-trigger {
  cursor: pointer;
}

.actions-grid {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.1rem;

  .btn-icon {
    padding: 0.4rem;
  }
}

.process-list-table {
  // Bootstrap fuerza `.table { width: 100% }`: si la suma de anchos de columna
  // es menor que el ancho del contenedor, el navegador reparte ese sobrante
  // entre las columnas (spec CSS2.1 17.5.2.2) ignorando los `width` de abajo.
  // Por eso los cambios de ancho "no se tomaban". Con `width: auto` la tabla
  // mide exactamente la suma de sus columnas y no sobra nada para repartir.
  width: auto !important;

  ::v-deep th,
  ::v-deep td {
    padding: 0.5rem 0.6rem;
    font-size: 0.9rem; // ← tamaño de letra general de toda la tabla
    vertical-align: middle;
  }

  ::v-deep thead th {
    font-size: 0.75rem; // ← tamaño de letra solo de los headers
  }

  // Shrinks to the width of the cell content instead of the (often longer)
  // header label; the header is allowed to wrap to two lines so it doesn't
  // force the column wider than the data actually needs.
  ::v-deep .process-col-compact {
    width: 1%;
  }

  ::v-deep th.process-col-compact {
    white-space: normal;
  }

  ::v-deep td.process-col-compact {
    white-space: nowrap;
  }

  // Igual que process-col-compact (shrink-to-content), pero con el header centrado —
  // exclusivo de la columna ID, a pedido de la usuaria (el resto de los headers quedan
  // alineados a la izquierda por defecto).
  ::v-deep .process-col-id {
    width: 1%;
  }

  ::v-deep th.process-col-id {
    white-space: normal;
    text-align: center;
  }

  ::v-deep td.process-col-id {
    white-space: nowrap;
  }

  ::v-deep .process-col-actions {
    width: 1%;
    white-space: nowrap;
    padding-left: 0rem !important;
    padding-right: 0rem !important;
  }

  ::v-deep th.process-col-actions {
    text-align: center;
  }

  ::v-deep .process-col-crimes {
    width: 20%;
    white-space: normal;
  }

  // A diferencia de compact: acá el contenido no es una sola palabra corta, sino una lista de
  // áreas unidas con " / " que puede volverse arbitrariamente larga. Con nowrap/shrink-to-content
  // (como compact) una fila con varias áreas estira toda la tabla y empuja el resto de columnas
  // fuera de vista. Con un ancho acotado + wrap, esa fila crece en alto en vez de en ancho.
  ::v-deep .process-col-areas {
    width: 15%;
    white-space: normal;
  }

  ::v-deep .process-col-wide {
    width: 12%;
    white-space: normal;
  }

  ::v-deep .process-col-name {
    width: 20%;
    white-space: normal;
  }

  ::v-deep .process-col-activity {
    width: 10%;
    white-space: normal;
  }
}

</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.controls-popover-list {
  font-size: 0.9rem;
  margin-bottom: 0;
  padding-left: 1.75rem;
  max-height: 250px;
  overflow-y: auto;

  li:not(:last-child) {
    margin-bottom: 0.5rem;
  }
}

.controls-responsibles-popover {
  max-width: 380px;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.25);

  .popover-body {
    font-size: 0.9rem;
  }
}

</style>
